import { useEffect, useRef, useState } from 'react';
import { Button, Row, Stack } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import AnalyzingProgressModal from 'src/components/AnalyzingProgressModal/AnalyzingProgressModal';
import PatientInfoWithControls from 'src/components/custom_components/PatientInfoWithControls';

import { Api } from 'src/models/Api.model';
import { CameraMode } from 'src/models/CameraMode.model';
import { Eye, EyePropertyCode } from 'src/models/Eye.model';
import { CancelStatus } from 'src/models/GalenData/CancelStatus.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { QualityPrecheckEnum } from 'src/models/Grading.model';
import { defaultBackendStatus } from 'src/models/InitialBackendStatus';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { EyesPosition } from 'src/models/Session.model';

import useCancelCaptureImages from 'src/hooks/camera/useCancelCaptureImages';
import useCaptureImage from 'src/hooks/camera/useCaptureImage';
import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useSubmitSession from 'src/hooks/useSubmitSession';
import useUiTranslation from 'src/hooks/useUiTranslation';

import { parseNewFiles } from 'src/utils/parseNewFiles';

import CameraPreviewColumn from './CameraPreviewColumn';
import ImagePreviewColumn from './ImagePreviewColumn';
import ImageSelectorColumn from './ImageSelectorColumn';
import ImageUploadColumn from './ImageUploadColumn';
import SmallPupilModal from './SmallPupilModal';

const SessionPreview = () => {
  const {
    leftUrl,
    rightUrl,
    setLeftUrl,
    setRightUrl,
    practiceSettings: { camera },
    setBackendStatus,
    setTotalLeftImageAttempt,
    setTotalRightImageAttempt,
    setIsAnalyzing,
    session,
    resetSession,
    qualityData,
    setQualityData,
    updateQualityData,
    cameraMode,
    isCapturing,
    isCanceling,
    isUploadingLeftImage,
    isUploadingRightImage,
    userSettings: { imageFolder },
  } = useRemoteBoundStore();

  const hasQualityPrecheck = false;

  const isClient = Api.isClient();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { tPages, tCommon, language } = useUiTranslation();

  const { userId, practiceId } = useGetCurrentUser();
  const { getErrorMessage } = useGetGalenErrorMsg();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const location = useLocation<{
    isResume: boolean;
    leftImageFileName: string;
    rightImageFileName: string;
  }>();

  const isResume = location.state?.isResume || false;

  const [newFiles, setNewFiles] = useState<string[]>([]);
  const [isShowAnalyzingModal, setIsShowAnalyzingModal] = useState(false);
  const [isQualityCheckDone, setIsQualityCheckDone] = useState(false);
  const [cancelStatus, setCancelStatus] = useState<CancelStatus>(
    CancelStatus.NotCanceled,
  );

  const {
    submitImage,
    pollQualityStatus,
    setUploadStatus,
    createRetakeSession,
    submitRetakeImage,
  } = useSubmitSession();

  const handleCloseAnalyzingModal = () => {
    setIsShowAnalyzingModal(false);
  };

  const handleSubmitSession = async () => {
    try {
      setIsShowAnalyzingModal(true);

      if (!practiceId) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
      }

      setBackendStatus(defaultBackendStatus);

      if (!userId) {
        throw new Error(tCommon('message.error.userIdNotFound'));
      }

      await setUploadStatus();

      setIsAnalyzing(true);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const { handleCancelCapture } = useCancelCaptureImages();

  const handleCancel = () => {
    Api.confirmBox({
      title: tCommon('bootbox.title.Confirm'),
      message: tPages('newExam.confirmExitImaging'),
      callback: (result) => {
        if (!result) {
          return;
        }
        setCancelStatus(CancelStatus.BothEyesCanceled);
        handleCancelCapture();
      },
      locale: getLocaleCode(language as Language),
    });
  };

  useEffect(() => {
    if (isResume) {
      if (session.leftImageFile) {
        setLeftUrl(session.leftImageFile);
      }

      if (session.rightImageFile) {
        setRightUrl(session.rightImageFile);
      }

      if (session.graded) {
        setIsShowAnalyzingModal(true);
      }
    }
    // We only want to run this effect when the component mounts, not when the component updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { captureLeftImage, captureRightImage, captureBothImages } =
    useCaptureImage();

  const handleRetakeCapture = (position: EyesPosition) => {
    if (position === EyesPosition.BOTH) {
      captureBothImages();
      setCancelStatus(CancelStatus.NotCanceled);
    }
    if (position === EyesPosition.LEFT) {
      setCancelStatus(CancelStatus.RightEyeCanceled);
      captureLeftImage();
    }
    if (position === EyesPosition.RIGHT) {
      setCancelStatus(CancelStatus.LeftEyeCanceled);
      captureRightImage();
    }
  };

  const handleRetake = async (position: EyesPosition) => {
    setIsQualityCheckDone(true); // stop polling before remote is set correctly
    if (position === EyesPosition.BOTH) {
      setLeftUrl('');
      setRightUrl('');

      // Have to put this after set_Url to avoid cypress clicking the submit button too quickly
      await createRetakeSession();
      if (cameraMode === CameraMode.API) {
        handleRetakeCapture(position);
      }
      return;
    }

    if (position === EyesPosition.LEFT) {
      setLeftUrl('');

      // Have to put this after set_Url to avoid cypress clicking the submit button too quickly
      const sessionId = await createRetakeSession();

      const basedata = {
        sessionId,
        side: EyePropertyCode.RIGHT,
        imageUrl: rightUrl,
        imageFileName: isResume
          ? location.state?.rightImageFileName
          : rightUrl.split('/').pop() || '',
      };

      const data = hasQualityPrecheck
        ? {
            ...basedata,
            qualityData: {
              RightEyeQuality: qualityData.RightEyeQuality,
              RightEyeQualityJson: qualityData.RightEyeQualityJson,
              LeftEyeQuality: QualityPrecheckEnum.Retake,
            },
          }
        : basedata;
      if (hasQualityPrecheck) {
        updateQualityData({ LeftEyeQuality: QualityPrecheckEnum.Retake });
        setIsQualityCheckDone(false);
      }
      submitRetakeImage(data);
      if (cameraMode === CameraMode.API) {
        handleRetakeCapture(position);
      }
      return;
    }

    if (position === EyesPosition.RIGHT) {
      setRightUrl('');

      // Have to put this after set_Url to avoid cypress clicking the submit button too quickly
      const sessionId = await createRetakeSession();

      const baseData = {
        sessionId,
        side: EyePropertyCode.LEFT,
        imageUrl: leftUrl,
        imageFileName: isResume
          ? location.state?.leftImageFileName
          : leftUrl.split('/').pop() || '',
      };

      const data = hasQualityPrecheck
        ? {
            ...baseData,
            qualityData: {
              LeftEyeQuality: qualityData.LeftEyeQuality,
              LeftEyeQualityJson: qualityData.LeftEyeQualityJson,
              RightEyeQuality: QualityPrecheckEnum.Retake,
            },
          }
        : baseData;

      if (hasQualityPrecheck) {
        updateQualityData({ RightEyeQuality: QualityPrecheckEnum.Retake });
        setIsQualityCheckDone(false);
      }
      submitRetakeImage(data);
      if (cameraMode === CameraMode.API) {
        handleRetakeCapture(position);
      }
    }
  };

  const resubmitExistingSession = async () => {
    const deviceDataId = await createRetakeSession();
    await Promise.all([
      submitImage({
        side: EyePropertyCode.RIGHT,
        imageUrl: rightUrl,
        imageFileName: rightUrl.split('/').pop() || '',
        hasQualityPrecheck,
        deviceDataId,
      }),
      submitImage({
        side: EyePropertyCode.LEFT,
        imageUrl: leftUrl,
        imageFileName: leftUrl.split('/').pop() || '',
        hasQualityPrecheck,
        deviceDataId,
      }),
    ]);
    setUploadStatus(deviceDataId);
  };

  useEffect(() => {
    if (
      hasQualityPrecheck &&
      (leftUrl || rightUrl) &&
      !intervalRef.current &&
      !isQualityCheckDone
    ) {
      const pollWithStopCondition = async () => {
        const qualityResult = await pollQualityStatus();

        const hasLeftQuality =
          qualityResult?.LeftEyeQuality !== undefined &&
          qualityResult?.LeftEyeQuality !== QualityPrecheckEnum.Retake;
        const hasRightQuality =
          qualityResult?.RightEyeQuality !== undefined &&
          qualityResult?.RightEyeQuality !== QualityPrecheckEnum.Retake;

        if (hasLeftQuality && hasRightQuality) {
          setIsQualityCheckDone(true);
          // Both quality data are available, stop polling
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      };
      intervalRef.current = setInterval(pollWithStopCondition, 1000);
    } else if (!hasQualityPrecheck && leftUrl && rightUrl) {
      // If quality precheck is disabled and we have both images, mark as done immediately
      setIsQualityCheckDone(true);
    }
  }, [
    leftUrl,
    pollQualityStatus,
    rightUrl,
    isQualityCheckDone,
    hasQualityPrecheck,
  ]);

  useEffect(() => {
    if (!session.leftImageFile) {
      setLeftUrl('');
    }

    if (!session.rightImageFile) {
      setRightUrl('');
    }

    let timer: NodeJS.Timeout;
    let fileCount = 0;

    const checkNewFiles = () => {
      Api.client.getNewFiles().then((files: string[]) => {
        if (files.length !== fileCount) {
          fileCount = files.length;
          files = files.filter(
            (file) =>
              file.toLowerCase().endsWith('.jpg') ||
              file.toLowerCase().endsWith('.jpeg'),
          );
          parseNewFiles(
            files,
            (imageUrl) => {
              submitImage({
                side: EyePropertyCode.LEFT,
                imageUrl,
                imageFileName: imageUrl.split('/').pop() || '',
                hasQualityPrecheck,
              });
            },
            (imageUrl) => {
              submitImage({
                side: EyePropertyCode.RIGHT,
                imageUrl,
                imageFileName: imageUrl.split('/').pop() || '',
                hasQualityPrecheck,
              });
            },
          );
          setNewFiles(files);
        }

        timer = setTimeout(checkNewFiles, 1000);
      });
    };

    // start monitoring when entering the page
    if (isClient && imageFolder && cameraMode === CameraMode.UPLOAD) {
      (async () => {
        try {
          await Api.client.startWatcher(imageFolder);
          console.log('watcher started successfully', imageFolder);
          checkNewFiles();
        } catch (err) {
          console.error('Error: failed to start watcher', err);
        }
      })();
    }

    return () => {
      // stop monitoring when leaving the page
      if (isClient) {
        Api.client.stopWatcher();
        if (timer) {
          clearInterval(timer);
        }
      }
    };
  }, [
    imageFolder,
    isClient,
    session.leftImageFile,
    session.rightImageFile,
    setLeftUrl,
    setRightUrl,
    cameraMode,
    submitImage,
    hasQualityPrecheck,
  ]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Reset image URLs and quality data to defaults on unmount
      setLeftUrl('');
      setRightUrl('');
      setTotalLeftImageAttempt(0);
      setTotalRightImageAttempt(0);
      if (hasQualityPrecheck) {
        setQualityData({
          LeftEyeQuality: undefined,
          LeftEyeQualityJson: undefined,
          RightEyeQuality: undefined,
          RightEyeQualityJson: undefined,
        });
      }
    };
  }, [
    setLeftUrl,
    setRightUrl,
    setQualityData,
    hasQualityPrecheck,
    setTotalLeftImageAttempt,
    setTotalRightImageAttempt,
  ]);

  return (
    <div className="d-flex flex-column h-100 overflow-hidden">
      <PatientInfoWithControls />
      <div
        className="d-flex gap-3 flex-fill overflow-auto"
        style={{ minHeight: 0 }}
      >
        {cameraMode === CameraMode.API && (
          <>
            <CameraPreviewColumn isExpanded={!!(leftUrl && rightUrl)} />
            <ImagePreviewColumn
              imageUrl={leftUrl}
              eyeType={Eye.LEFT}
              onRetake={() => {
                handleRetakeCapture(EyesPosition.LEFT);
              }}
              retakeLabel={tPages('newExam.retakeLeftEye')}
              isExpanded={!!(leftUrl && rightUrl)}
              isRetakeDisabled={isCapturing}
              isCanceling={isCanceling}
              hasCanceled={
                cancelStatus === CancelStatus.LeftEyeCanceled ||
                cancelStatus === CancelStatus.BothEyesCanceled
              }
              quality={
                hasQualityPrecheck ? qualityData.LeftEyeQuality : undefined
              }
              hasQualityPrecheck={hasQualityPrecheck}
            />
            <ImagePreviewColumn
              imageUrl={rightUrl}
              eyeType={Eye.RIGHT}
              onRetake={() => {
                handleRetakeCapture(EyesPosition.RIGHT);
              }}
              retakeLabel={tPages('newExam.retakeRightEye')}
              isExpanded={!!(leftUrl && rightUrl)}
              isRetakeDisabled={isCapturing}
              isCanceling={isCanceling}
              hasCanceled={
                cancelStatus === CancelStatus.RightEyeCanceled ||
                cancelStatus === CancelStatus.BothEyesCanceled
              }
              quality={
                hasQualityPrecheck ? qualityData.RightEyeQuality : undefined
              }
              hasQualityPrecheck={hasQualityPrecheck}
            />
          </>
        )}
        {cameraMode === CameraMode.UPLOAD && (
          <>
            <ImageSelectorColumn
              isClient={isClient}
              imageFolder={imageFolder}
              camera={camera}
              newFiles={newFiles}
              leftUrl={leftUrl}
              rightUrl={rightUrl}
              onImageSelect={(imageUrl, eye) => {
                if (eye === EyePropertyCode.LEFT) {
                  submitImage({
                    side: EyePropertyCode.LEFT,
                    imageUrl,
                    imageFileName: imageUrl.split('/').pop() || '',
                    hasQualityPrecheck,
                  });
                } else if (eye === EyePropertyCode.RIGHT) {
                  submitImage({
                    side: EyePropertyCode.RIGHT,
                    imageUrl,
                    imageFileName: imageUrl.split('/').pop() || '',
                    hasQualityPrecheck,
                  });
                }
                setIsQualityCheckDone(false);
              }}
            />

            <ImageUploadColumn
              imageUrl={leftUrl}
              eyeLabel={tPages('newExam.leftEye')}
              uploadLabel={tPages('newExam.upload')}
              quality={
                hasQualityPrecheck ? qualityData.LeftEyeQuality : undefined
              }
              id={EyePropertyCode.LEFT}
              hasQualityPrecheck={hasQualityPrecheck}
              isUploading={isUploadingLeftImage}
              onImageSelect={(imageUrl) => {
                submitImage({
                  side: EyePropertyCode.LEFT,
                  imageUrl,
                  imageFileName: imageUrl.split('/').pop() || '',
                  hasQualityPrecheck,
                });
                setIsQualityCheckDone(false);
              }}
            />

            <ImageUploadColumn
              imageUrl={rightUrl}
              eyeLabel={tPages('newExam.rightEye')}
              uploadLabel={tPages('newExam.upload')}
              quality={
                hasQualityPrecheck ? qualityData.RightEyeQuality : undefined
              }
              id={EyePropertyCode.RIGHT}
              hasQualityPrecheck={hasQualityPrecheck}
              isUploading={isUploadingRightImage}
              onImageSelect={async (imageUrl) => {
                submitImage({
                  side: EyePropertyCode.RIGHT,
                  imageUrl,
                  imageFileName: imageUrl.split('/').pop() || '',
                  hasQualityPrecheck,
                });
                setIsQualityCheckDone(false);
              }}
            />
          </>
        )}
      </div>
      <Row style={{ marginTop: 'auto' }}>
        <Stack direction="horizontal" gap={3} className="d-flex pt-2">
          <div className="d-flex gap-3">
            {cameraMode === CameraMode.API && (
              <Button
                variant="danger"
                disabled={!isCapturing || isCanceling}
                onClick={() => {
                  handleCancel();
                }}
              >
                {isCanceling
                  ? tPages('newExam.cancelingImaging')
                  : tPages('newExam.cancelImaging')}
              </Button>
            )}
          </div>
          <div className="ms-auto d-flex gap-3">
            {cameraMode === CameraMode.API && (
              <Button
                variant="primary-100"
                onClick={() => {
                  handleRetakeCapture(EyesPosition.BOTH);
                }}
                disabled={isCapturing || isCanceling}
              >
                {tPages('newExam.retake')}
              </Button>
            )}
            <Button
              variant="primary"
              className="w-200px shadow"
              onClick={() => {
                handleSubmitSession();
              }}
              disabled={
                !leftUrl ||
                !rightUrl ||
                isUploadingLeftImage ||
                isUploadingRightImage ||
                (hasQualityPrecheck && !isQualityCheckDone)
              }
            >
              {tPages('newExam.submit')}
            </Button>
          </div>
        </Stack>
      </Row>

      {isShowAnalyzingModal && (
        <AnalyzingProgressModal
          handleRetake={handleRetake}
          session={session}
          handleCloseAnalyzingProgressModal={handleCloseAnalyzingModal}
          handleResetSession={resetSession}
          resubmitExistingSession={resubmitExistingSession}
        />
      )}
      <SmallPupilModal />
    </div>
  );
};

export default SessionPreview;
